'use client';
import { cn } from '@/lib/utils';
import Image from 'next/image';
import { useEffect, useState } from 'react';
import { Skeleton } from '../ui/skeleton';
import Link from 'next/link';
import { Separator } from '../ui/separator';

let viewCount = 25350635;

const quickLinks = [
  {
    href: 'https://www.youtube.com/playlist?list=PLfhxxUq_8RG6SqC25cnN1cSmESxs0nzgX',
    icon: '/assets/icons/nav/daily-message.png',
    label: 'Daily Message',
  },
  {
    href: '/blogs/30-day-faith-journey',
    icon: '/assets/icons/nav/ocia-classes.png',
    label: (
      <>
        OCIA Classes with <em>Fr. Ed</em>
      </>
    ),
  },
  {
    href: 'https://www.youtube.com/@SMARTGUYAcademy/community',
    icon: '/assets/icons/nav/member-polls.png',
    label: 'Member Polls',
  },
];

const fetchYouTubeStats = async () => {
  try {
    const cacheKey = new Date().toISOString().slice(0, 10);
    const res = await fetch(
      `${process.env.NEXT_PUBLIC_API_URL}/videos/youtube-stats?cacheKey=${cacheKey}`,
      {
        cache: 'no-store',
      }
    );

    if (res.ok) {
      const data = await res.json();
      return data?.data?.totalViews || viewCount;
    }
    return null;
  } catch (err) {
    return null;
  }
};

function LogoImage({ className, ...props }) {
  return (
    <>
      <div className={cn('relative', className ? className : 'h-20 w-xs')}>
        <Image
          src="/assets/images/logos/logo-white.png"
          alt="Logo"
          fill
          className="object-contain"
          priority
        />
      </div>
    </>
  );
}

function LogoText({ className, ...props }) {
  return (
    <>
      <span className={cn('text-primary font-bold', className)} {...props}>
        SMARTGUY
      </span>
    </>
  );
}

function LogoRegister({ className, ...props }) {
  return (
    <span className={cn('text-primary font-bold', className)} {...props}>
      {/* &reg; */}
    </span>
  );
}

function LogoLinks({ className, showLinks = false, ...props }) {
  const [dailyMessageUrl, setDailyMessageUrl] = useState('/daily-message');

  useEffect(() => {
    let isMounted = true;

    const fetchDailyMessageUrl = async () => {
      try {
        const res = await fetch('/api/daily-message/latest');
        if (!res.ok) return;
        const data = await res.json();
        const url = data?.data?.url;
        if (isMounted && url) {
          setDailyMessageUrl(url);
        }
      } catch {
        // keep fallback /daily-message
      }
    };

    fetchDailyMessageUrl();

    return () => {
      isMounted = false;
    };
  }, []);

  const isDailyMessageExternal = /^https?:\/\//i.test(dailyMessageUrl);

  return (
    <div
      className={cn(
        'mx-auto flex flex-wrap items-center justify-center',
        showLinks ? 'border-b border-gray-300' : 'border-0',
        className
      )}
      {...props}
    >
      {!showLinks && (
        <div className="flex flex-row gap-x-2">
          <a
            href={dailyMessageUrl}
            target={isDailyMessageExternal ? '_blank' : undefined}
            rel={isDailyMessageExternal ? 'noopener noreferrer' : undefined}
            className="flex items-center justify-center"
          >
            <span className="text-primary text-[.7rem] font-medium italic underline">
              Daily Message
            </span>
          </a>

          <a
            href="/blogs/30-day-faith-journey"
            target="_blank"
            className="flex items-center justify-center"
          >
            <span className="text-primary text-xs font-medium italic underline">
              OCIA Classes with Fr. Ed
            </span>
          </a>

          <a
            href="https://www.youtube.com/@SMARTGUYAcademy/community"
            target="_blank"
            className="flex items-center justify-center"
          >
            <span className="text-primary text-xs font-medium italic underline">Member Polls</span>
          </a>

          {/* <Link href="/community" className="flex items-center justify-center">
        <span
          className={cn('text-primary text-[.7rem] font-medium italic underline', className)}
          {...props}
        >
          Community
        </span>
      </Link> */}
        </div>
      )}

      {showLinks &&
        quickLinks.map((link, index) => {
          const href = index === 0 ? dailyMessageUrl : link.href;
          const isExternal = /^https?:\/\//i.test(href);

          return (
            <a
              key={index}
              href={href}
              target={isExternal ? '_blank' : undefined}
              rel={isExternal ? 'noopener noreferrer' : undefined}
              className="flex items-center gap-2 border-l border-gray-300 px-4 py-2.5 first:border-l-0 sm:px-6"
            >
              <Image src={link.icon} alt="" width={22} height={22} className="shrink-0" />
              <span className="text-primary text-xs font-bold sm:text-sm">{link.label}</span>
            </a>
          );
        })}
    </div>
  );
}

// Home page Logo
const LogoLarge = () => {
  const [visitorsCount, setVisitorsCount] = useState(null);

  useEffect(() => {
    let isMounted = true;
    const fetchDataAsync = async () => {
      const data = await fetchYouTubeStats();
      if (isMounted && data !== null) {
        setVisitorsCount(data);
      }
    };

    fetchDataAsync();

    // Cleanup function to prevent state updates on unmounted component
    return () => {
      isMounted = false;
    };
  }, []);

  return (
    <div className="flex flex-col items-center justify-center">
      <a href="/" className="flex flex-col items-center justify-center">
        <div className="relative aspect-[3/1] h-20 w-xs sm:h-20 sm:w-xs xl:h-20 xl:w-xs">
          <Image
            src="/assets/images/logos/logo-white.png"
            alt="Logo"
            fill
            className="object-contain"
            priority
          />
        </div>

        {/* <div className="relative aspect-[3/1] h-20 w-xs sm:h-20 sm:w-sm xl:h-40 xl:w-md">
          <Image
            src="/assets/images/logos/logo-white.png"
            alt="Logo"
            fill
            className="object-contain"
            priority
          />
        </div> */}
        {/* <span className="text-md text-primary font-semibold sm:text-lg xl:-mt-4 xl:text-2xl">
          FAITH, WISDOM AND COMMUNITY.
        </span> */}
        {/* <span
          className={cn(
            'font-figtree text-md text-primary mb-4 font-bold',
            'sm:text-lg',
            'xl:-mt-4 xl:text-2xl'
          )}
        >
          Catholic Media and Learning Company
        </span> */}
        {/* <span className="font-figtree text-md text-primary font-semibold sm:text-lg xl:-mt-4 xl:text-2xl">
          {!visitorsCount ? (
            <Skeleton className={'block h-6 w-40 sm:h-8 sm:w-50'} />
          ) : (
            `${visitorsCount.toLocaleString('en-US')} People Strong!`
          )}
        </span> */}
      </a>

      {/* <div className="flex w-full flex-row items-center justify-center gap-2">
        <div className="hidden w-19 sm:block">
          <Separator orientation="horizontal" className="border-[1.5px]" />
        </div>

        <div className="block">
          <Link
            href="https://www.youtube.com/@SmartGuyAcademy"
            target="_blank"
            rel="noopener noreferrer"
            className="pt-2 pb-2 underline"
          >
            <span className="font-figtree text-primary hover:text-primary/80 text-sm font-semibold italic hover:underline lg:text-xl">
              Join 1M+ YouTube Subscribers
            </span>
          </Link>
        </div>
        <div className="hidden w-19 sm:block">
          <Separator orientation="horizontal" className="border-[1.5px]" />
        </div>
      </div> */}

      {/* <LogoLinks className="mt-3" showLinks={true} /> */}
    </div>
  );
};

function LogoSlogan({ className, ...props }) {
  const [visitorsCount, setVisitorsCount] = useState(null);

  useEffect(() => {
    let isMounted = true;
    const fetchDataAsync = async () => {
      const data = await fetchYouTubeStats();
      if (isMounted && data !== null) {
        setVisitorsCount(data);
      }
    };

    fetchDataAsync();

    // Cleanup function to prevent state updates on unmounted component
    return () => {
      isMounted = false;
    };
  }, []);

  return (
    <>
      <span className={cn('text-primary text-xs font-semibold', className)} {...props}>
        FAITH, WISDOM AND COMMUNITY.
      </span>
      <span className={cn('text-primary font-bold italic', className)} {...props}>
        {/* Faith, Family, Growth, Unity. */}
        {/* 13,081,095 visitors */}
        {!visitorsCount ? (
          <Skeleton className={'block h-3.5 w-40'} />
        ) : (
          `${visitorsCount.toLocaleString('en-US')} Strong!`
        )}
        {/* {visitorsCount.toLocaleString('en-US')} Strong! */}
        {/* <strong>visitors</strong> */}
      </span>
    </>
  );
}

export { LogoLarge, LogoText, LogoImage, LogoRegister, LogoSlogan, LogoLinks, fetchYouTubeStats };
